<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";

import { toast } from "@/common/utils/toast";

import { userActivate } from "@/common/api/rest/user";

const route = useRoute();
const router = useRouter();

const status = ref("loading");
const errorMessage = ref("");

const ativarConta = async () => {
  const hash = route.query.hash;

  if (!hash) {
    status.value = "error";
    errorMessage.value = "Hash de ativação não encontrado ou inválido.";
    return;
  }

  await userActivate(hash)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        status.value = "success";
      } else {
        status.value = "error";
        toast({ message: message, type: "error" });
      }
    }).catch((error) => {
      console.log(error);
      status.value = "error";
      toast({
        message: "Ocorreu um erro ao ativar sua conta. O link pode ter expirado.",
        type: "error"
      });
    });
};

const irParaLogin = () => {
  router.push("/auth/login");
};

onMounted(() => {
  ativarConta();
});
</script>

<template>
  <div class="activation-container">
    <div class="activation-card">
      <div v-if="status === 'loading'" class="state-content">
        <div class="spinner"></div>
        <h2>Verificando seu link...</h2>
        <p>
          Estamos validando suas informações para ativar sua conta. Aguarde um momento.
        </p>
      </div>

      <div v-else-if="status === 'success'" class="state-content success">
        <div class="icon-wrapper">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        <h2>Conta Ativada com Sucesso!</h2>
        <p>
          Parabéns! Sua conta está pronta e você já pode aproveitar todos os nossos recursos.
        </p>
        <button class="btn btn-primary" @click="irParaLogin">
          Acessar minha conta
        </button>
      </div>

      <div v-else-if="status === 'error'" class="state-content error">
        <div class="icon-wrapper">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </div>
        <h2>Ops! Algo deu errado</h2>
        <p>{{ errorMessage }}</p>
        <button
          class="btn btn-secondary"
          @click="ativarConta"
          v-if="route.query.token"
        >
          Tentar novamente
        </button>
        <button class="btn btn-link" @click="irParaLogin">
          Ir para o Login
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Variáveis de Design
$primary-color: #4f46e5;
$primary-hover: #4338ca;
$success-color: #10b981;
$error-color: #ef4444;
$text-main: #1f2937;
$text-muted: #6b7280;
$bg-gradient: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);

.activation-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: $bg-gradient;
  padding: 1.5rem;
  font-family:
    system-ui,
    -apple-system,
    sans-serif;
}

.activation-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  padding: 2.5rem;
  width: 100%;
  max-width: 450px;
  text-align: center;
  transition: all 0.3s ease;
}

.state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: fadeIn 0.5s ease-in-out;

  h2 {
    color: $text-main;
    font-size: 1.5rem;
    margin: 1.5rem 0 0.75rem;
    font-weight: 700;
  }

  p {
    color: $text-muted;
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 2rem;
  }
}

// Ícones estruturados com SCSS Nesting
.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;

  svg {
    width: 32px;
    height: 32px;
  }

  .success & {
    background-color: rgba($success-color, 0.1);
    color: $success-color;
  }

  .error & {
    background-color: rgba($error-color, 0.1);
    color: $error-color;
  }
}

// Botões configurados de forma modular
.btn {
  width: 100%;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition:
    background-color 0.2s,
    transform 0.1s;

  &:active {
    transform: scale(0.98);
  }

  &-primary {
    background-color: $primary-color;
    color: white;

    &:hover {
      background-color: $primary-hover;
    }
  }

  &-secondary {
    background-color: #f3f4f6;
    color: $text-main;

    &:hover {
      background-color: #e5e7eb;
    }
  }

  &-link {
    background: transparent;
    color: $primary-color;
    margin-top: 0.5rem;
    text-decoration: underline;

    &:hover {
      color: $primary-hover;
    }
  }
}

// Spinner de carregamento puro CSS
.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba($primary-color, 0.1);
  border-left-color: $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

// Animações
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
